import { useEffect } from 'react';
import i18n, { resolveUiLanguage } from '@/i18n';
import { getProfileLocale } from '@/api/locale.api';

/**
 * S4-35 (I18N-PANEL, инвариант I1): язык интерфейса залогиненного = язык профиля.
 *
 * Монтируется в оболочке залогиненного (`Layout`). Браузерный детектор
 * остаётся только первичной догадкой: как только профиль отвечает
 * непустым `language`, интерфейс переключается на него.
 *
 * @contract Единственный писатель языка у залогиненного — ось профиля
 *   (`user_profile.language`); переключателя в шапке у него нет.
 * @requires Вызывается только под авторизацией (эндпоинт `/profile/locale` за JWT).
 * @ensures `language !== null` → `i18n.changeLanguage(resolveUiLanguage(language))`.
 * @ensures `language === null` (не задан / отклонён) → язык не трогается,
 *   работает детектор (localStorage → navigator).
 * @ensures Ошибка сети/сервиса → язык не трогается (молча: это не действие
 *   пользователя, показывать ошибку негде и незачем).
 * @modifies Язык i18next (и его кэш в localStorage).
 * @business_impact Без синхронизации человек, выбравший язык в «Язык и
 *   регион», видит интерфейс на языке браузера — два регулятора расходятся.
 */
export function useProfileLanguageSync(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getProfileLocale()
      .then((locale) => {
        if (cancelled || !locale.language) return;
        const target = resolveUiLanguage(locale.language);
        if (i18n.resolvedLanguage !== target) void i18n.changeLanguage(target);
      })
      .catch(() => {
        // Нет ответа профиля — остаёмся на языке детектора.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
